/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import Backbone from 'app/common/vendor/backbone';
import React, { FunctionComponent, useState, Suspense } from 'react';
import { getMe } from 'app/gamma/src/selectors/members';
import { connect } from 'react-redux';

import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import { State } from 'app/gamma/src/modules/types';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos';

import { forNamespace } from 'app/common/lib/util/i18n';
import { useLazyComponent } from '@trello/use-lazy-component';

const viewTitle = forNamespace('view title');

interface ChannelSwitcherButtonProps {
  member: MemberModel | undefined;
  hostname: string;
}

// Show channel picker if user is allowed to view multiple channels
// OR if we're not on production (to make development easier)
const shouldShowChannelPicker = (
  member: MemberModel | undefined,
  hostname: string,
): boolean => {
  if (hostname !== 'trello.com') {
    return true;
  }

  return !!(member && member.channels && member.channels.allowed.length > 1);
};

enum Screen {
  ChannelSwitcherScreen,
  FeatureFlagScreen,
  HydraScreen,
  ExperimentScreen,
  ExperimentDetailsScreen,
  OneTimeMessagesDismissedScreen,
}

export const ChannelSwitcherButton: FunctionComponent<ChannelSwitcherButtonProps> = ({
  member,
  hostname,
}) => {
  const { toggle, triggerRef, popoverProps, push } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.ChannelSwitcherScreen,
  });
  const [selectedExperiment, setSelectedExpirement] = useState('');
  const showButton = shouldShowChannelPicker(member, hostname);

  const ChannelSwitcher = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "channel-switcher-popover" */ 'app/gamma/src/components/popovers/channel-switcher/channel-switcher'
      ),
    { preload: showButton, namedImport: 'ChannelSwitcher' },
  );

  const ExperimentList = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "experiment-list-menu" */ 'app/gamma/src/components/popovers/channel-switcher/ExperimentList'
      ),
    { preload: popoverProps.isVisible, namedImport: 'ExperimentList' },
  );

  const FeatureFlagList = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "feature-flag-list" */ 'app/gamma/src/components/popovers/channel-switcher/FeatureFlagList'
      ),
    { preload: popoverProps.isVisible, namedImport: 'FeatureFlagList' },
  );

  const HydraChooser = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "hydra-chooser-menu" */ 'app/gamma/src/components/popovers/hydra-chooser'
      ),
    { preload: popoverProps.isVisible },
  );

  const ExperimentDetails = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "experiment-details-menu" */ 'app/gamma/src/components/popovers/experiment-details'
      ),
    { preload: popoverProps.isVisible },
  );

  const OneTimeMessagesDismissedList = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "onetime-messages-menu" */ 'app/src/components/OneTimeMessagesDismissedList'
      ),
    {
      namedImport: 'OneTimeMessagesDismissedList',
      preload: popoverProps.isVisible,
    },
  );

  if (!showButton) {
    return null;
  }

  return (
    <>
      <HeaderButton
        icon={IconName.Gear}
        onClick={toggle}
        ref={triggerRef}
        testId={HeaderTestIds.ChannelPickerButton}
      />
      <Popover {...popoverProps} testId={HeaderTestIds.ChannelPickerPopover}>
        <Suspense fallback={null}>
          <PopoverScreen
            id={Screen.ChannelSwitcherScreen}
            title={viewTitle('switch channels')}
          >
            <ChannelSwitcher
              onSelectHydraChooser={() => push(Screen.HydraScreen)}
              onSelectFeatureFlags={() => push(Screen.FeatureFlagScreen)}
              onSelectExperiments={() => push(Screen.ExperimentScreen)}
              onSelectOneTimeMessagesDismissed={() =>
                push(Screen.OneTimeMessagesDismissedScreen)
              }
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.HydraScreen}
            title={viewTitle('select client version')}
          >
            <HydraChooser />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.FeatureFlagScreen}
            title={viewTitle('feature flags')}
          >
            <FeatureFlagList />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.ExperimentScreen}
            title={viewTitle('experiments')}
          >
            <ExperimentList
              onSelectExperiment={(experimentName) => {
                setSelectedExpirement(experimentName);
                push(Screen.ExperimentDetailsScreen);
              }}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.ExperimentDetailsScreen}
            title={viewTitle('experiment config')}
          >
            <ExperimentDetails experimentName={selectedExperiment} />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.OneTimeMessagesDismissedScreen}
            title="oneTimeMessagesDismissed"
          >
            <OneTimeMessagesDismissedList memberId={member?.id ?? ''} />
          </PopoverScreen>
        </Suspense>
      </Popover>
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    member: getMe(state),
    hostname: Backbone.history.location.hostname,
  };
};

export default connect(mapStateToProps)(ChannelSwitcherButton);
